import React, { useEffect, useState } from 'react';
import { purchaseAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Ruler, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function DimensionVariancePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await purchaseAPI.getDimensionVariances();
      setRows(res.data.variances || []);
    } catch (e) { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="dimension-variance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Ruler className="w-5 h-5" /> Dimension Variances</h1>
          <p className="text-xs text-muted-foreground">Supplier-delivered vs ordered sheet dimensions, captured when a PO is amended. Use for future negotiations.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="variance-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground" data-testid="variance-empty">No dimension variances recorded yet. They are logged automatically when you amend a PO's sheet size or item on a cut-to-size line.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 border-b">
                  <tr className="text-left">
                    <th className="p-2.5">Date</th>
                    <th className="p-2.5">PO#</th>
                    <th className="p-2.5">Supplier</th>
                    <th className="p-2.5">Ordered</th>
                    <th className="p-2.5">Delivered</th>
                    <th className="p-2.5">Grade / Thk</th>
                    <th className="p-2.5">Qty</th>
                    <th className="p-2.5">Reason</th>
                    <th className="p-2.5">By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-b hover:bg-muted/20" data-testid={`variance-row-${i}`}>
                      <td className="p-2.5 whitespace-nowrap">{r.recorded_at ? new Date(r.recorded_at).toLocaleDateString() : ''}</td>
                      <td className="p-2.5 font-mono">{r.po_number || r.pr_number}</td>
                      <td className="p-2.5">{r.supplier_name}</td>
                      <td className="p-2.5">
                        <div className="font-medium">{r.ordered_sheet_size || '—'}</div>
                        <div className="text-[10px] text-muted-foreground">{r.ordered_unique_id}</div>
                      </td>
                      <td className="p-2.5">
                        <div className="font-medium text-amber-700">{r.delivered_sheet_size || '—'}</div>
                        <div className="text-[10px] text-muted-foreground">{r.delivered_unique_id}</div>
                      </td>
                      <td className="p-2.5 whitespace-nowrap">{r.grade}{r.thickness != null ? ` · ${r.thickness}mm` : ''}</td>
                      <td className="p-2.5">{r.quantity}</td>
                      <td className="p-2.5 max-w-[220px] truncate" title={r.reason}>{r.reason || '—'}</td>
                      <td className="p-2.5 whitespace-nowrap">{r.recorded_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
